import { TableRow } from '@mui/material';
import { memo } from 'react';
import useHeightObserver from '@repo/hooks/useHeightObserver';
import { AnalyticsSkuViewed } from '@repo/segment/Analytics/AnalyticsSkuViewed';
import { SEGMENT_SKU_VIEWED_SOURCE } from '@repo/segment/Analytics/constants';
import { MarketplaceEntityTypeEnum, MonetizableEnum } from '@repo/supergraph/marketplace/enums';
import { useBestOfAwardsConfig } from '@repo/ui/BestOfAwards/Context';
import { BestOfAwardsRoundupBanner } from '@repo/ui/BestOfAwards/Roundup';
import type { SummaryTableRowWithDrivers } from '../../types';
import { DetailsCell } from './DetailsCell';
import { ProductCell } from './ProductCell';

interface RowProps {
  index: number;
  row: SummaryTableRowWithDrivers;
}

export const Row = memo(
  ({ index, row }: RowProps) => {
    const { marketplaceEntity } = row;
    const {
      institution,
      entityType,
      id,
      offerForCta,
      product,
      productType,
      name,
      monetizable: defaultMonetizable = MonetizableEnum.no,
      drivers,
      subProductType,
    } = marketplaceEntity;

    const monetizable = offerForCta?.monetizable ?? defaultMonetizable;

    const offerId = offerForCta?.id ? offerForCta.id : id;
    const offerName = offerForCta?.name ? offerForCta.name : name;

    const analyticsPayload = {
      index,
      ...(institution?.id && { institutionId: institution.id }),
      ...(entityType === MarketplaceEntityTypeEnum.Institutions && { institutionId: id }),
      offerId,
      ...(product?.id && { productId: product.id }),
      productType,
      monetizable,
    };

    const { hasBestOfAward, config: bestOfAwardsConfig } = useBestOfAwardsConfig(offerId, {
      institutionId: institution?.id,
      subProductType,
    });
    const bestOfAwardsRef = useHeightObserver({
      onChangeHeight: (height, target) => {
        // Scope CSS variable to the current row
        target?.closest('tr')?.style.setProperty('--cell-padding-top', `${height}px`);
      },
    });

    return (
      <AnalyticsSkuViewed
        source={SEGMENT_SKU_VIEWED_SOURCE}
        name={offerName}
        productType={productType}
        level="offer"
        {...(institution && {
          institutionId: institution.id,
          institutionName: institution.name,
        })}
        {...(entityType === MarketplaceEntityTypeEnum.Institutions && {
          institutionId: id,
          institutionName: name,
        })}
        {...(product && {
          productId: product.id,
          productName: product.name,
        })}
        offerId={offerId}
        offerName={offerName}
        monetizable={monetizable}
        impressionSection="summary_table_section"
        impressionPosition={index}
        additionalAttributes={{
          page_type: 'roundup',
        }}
      >
        <TableRow
          sx={{
            '&:last-of-type td': {
              borderBottom: 'none',
            },
            position: hasBestOfAward ? 'sticky' : undefined,
          }}
        >
          <ProductCell
            analyticsPayload={analyticsPayload}
            marketplaceEntity={marketplaceEntity}
            beforeMainContent={
              hasBestOfAward ? (
                <BestOfAwardsRoundupBanner
                  variant="row-compact"
                  ref={bestOfAwardsRef}
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                  }}
                >
                  {bestOfAwardsConfig.awardText}
                </BestOfAwardsRoundupBanner>
              ) : null
            }
          />
          <DetailsCell drivers={drivers} />
        </TableRow>
      </AnalyticsSkuViewed>
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.row.marketplaceEntity.id === nextProps.row.marketplaceEntity.id &&
      prevProps.index === nextProps.index
    );
  },
);
